/**
 * RowActionsMenu — меню «⋯» строки платежа (поповер). Пункты:
 *   Внести оплату · Редактировать · Перенести срок · История изменений · Отменить платёж · Удалить (красный).
 *
 * Видимость/доступность пунктов зависит от прав (canEdit) и замка оплаченного (locked → нужен edit_paid).
 * Сами действия исполняет родитель через колбэки (открытие диалогов/мутации).
 */

import { useState } from 'react';
import type { PaymentDto } from '../../lib/types';
import { Popover } from './ui/Popover';
import { cx } from './ui/cx';
import {
  IconBan,
  IconDots,
  IconHistory,
  IconLock,
  IconMoney,
  IconPencil,
  IconClock,
  IconTrash,
} from './ui/icons';

export interface RowActionsMenuProps {
  payment: PaymentDto;
  canEdit: boolean;
  /** Транш под замком оплаченного и у пользователя нет edit_paid. */
  locked: boolean;
  onRegisterPayment: (p: PaymentDto) => void;
  onEdit: (p: PaymentDto) => void;
  onReschedule: (p: PaymentDto) => void;
  onHistory: (p: PaymentDto) => void;
  onCancel: (p: PaymentDto) => void;
  onDelete: (p: PaymentDto) => void;
}

interface MenuItem {
  key: string;
  label: string;
  icon: JSX.Element;
  onClick: () => void;
  danger?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

export function RowActionsMenu({
  payment,
  canEdit,
  locked,
  onRegisterPayment,
  onEdit,
  onReschedule,
  onHistory,
  onCancel,
  onDelete,
}: RowActionsMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const close = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  const isCancelled = payment.status === 'CANCELLED';
  const isPaid = payment.status === 'PAID';
  // Правка/оплата закрыты, если нет edit вообще либо транш под замком без edit_paid.
  const mutationBlocked = !canEdit || locked;

  const items: MenuItem[] = [
    {
      key: 'pay',
      label: 'Внести оплату',
      icon: <IconMoney size={16} />,
      onClick: close(() => onRegisterPayment(payment)),
      hidden: !canEdit || isCancelled || isPaid,
      disabled: locked,
    },
    {
      key: 'edit',
      label: 'Редактировать',
      icon: <IconPencil size={16} />,
      onClick: close(() => onEdit(payment)),
      hidden: !canEdit || isCancelled,
      disabled: locked,
    },
    {
      key: 'reschedule',
      label: 'Перенести срок',
      icon: <IconClock size={16} />,
      onClick: close(() => onReschedule(payment)),
      hidden: !canEdit || isCancelled,
      disabled: locked,
    },
    {
      key: 'history',
      label: 'История изменений',
      icon: <IconHistory size={16} />,
      onClick: close(() => onHistory(payment)),
    },
    {
      key: 'cancel',
      label: 'Отменить платёж',
      icon: <IconBan size={16} />,
      onClick: close(() => onCancel(payment)),
      hidden: !canEdit || isCancelled,
      disabled: mutationBlocked,
    },
    {
      key: 'delete',
      label: 'Удалить',
      icon: <IconTrash size={16} />,
      onClick: close(() => onDelete(payment)),
      danger: true,
      hidden: !canEdit,
      disabled: mutationBlocked && !isCancelled,
    },
  ];

  const visible = items.filter((i) => !i.hidden);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      role="menu"
      label={`Действия по траншу №${payment.number}`}
      trigger={
        <button
          type="button"
          className="pw-icon-btn pw-row-actions__trigger"
          aria-label={`Действия по траншу №${payment.number}`}
          title="Действия"
        >
          {locked ? <IconLock size={15} /> : <IconDots size={16} />}
        </button>
      }
      panelClassName="pw-menu"
    >
      {visible.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={cx('pw-menu__item', item.danger && 'pw-menu__item--danger')}
          onClick={item.onClick}
          disabled={item.disabled}
        >
          <span className="pw-menu__icon" aria-hidden>
            {item.icon}
          </span>
          {item.label}
        </button>
      ))}
    </Popover>
  );
}
